/**
 * ProfileTaskGenerationService
 * 
 * Generates Epic Journey tasks based on missing profile fields to ensure
 * profile completeness is tracked and actionable across the system.
 */

import { profileDataService } from './profileDataService.js';
import StatusMappingService from './StatusMappingService.js';
import DateSynchronizationService from './DateSynchronizationService.js';

class ProfileTaskGenerationService {
  /**
   * Profile sections and their task generation rules
   */
  static PROFILE_SECTIONS = {
    basicInfo: {
      label: 'Basic Information',
      fields: ['first_name', 'last_name', 'email', 'phone', 'birth_date'],
      taskTitle: 'Complete Basic Profile Information',
      priority: 'high',
      estimatedMinutes: 10
    },
    schoolInfo: {
      label: 'School Information',
      fields: ['high_school_name', 'graduation_year', 'school_type', 'school_city', 'school_state'],
      taskTitle: 'Add High School Information',
      priority: 'high',
      estimatedMinutes: 15
    },
    academicPerformance: {
      label: 'Academic Performance',
      fields: ['gpa', 'gpa_scale', 'class_rank', 'class_size', 'transcript_uploaded'],
      taskTitle: 'Update Academic Performance Metrics',
      priority: 'high',
      estimatedMinutes: 20
    },
    standardizedTests: {
      label: 'Test Scores',
      fields: ['sat_score', 'act_score', 'ap_scores', 'ib_scores'],
      taskTitle: 'Add Standardized Test Scores',
      priority: 'medium',
      estimatedMinutes: 15
    },
    coursework: {
      label: 'Coursework',
      fields: ['ap_courses', 'honors_courses', 'dual_enrollment', 'current_courses'],
      taskTitle: 'Document Advanced Coursework',
      priority: 'medium',
      estimatedMinutes: 30
    },
    extracurriculars: {
      label: 'Activities',
      fields: ['activities', 'leadership_roles', 'volunteer_hours', 'work_experience'],
      taskTitle: 'Add Extracurricular Activities',
      priority: 'high',
      estimatedMinutes: 45
    },
    awards: {
      label: 'Awards & Honors',
      fields: ['academic_awards', 'extracurricular_awards', 'recognition'],
      taskTitle: 'List Awards and Achievements',
      priority: 'medium',
      estimatedMinutes: 20
    },
    personalStatement: {
      label: 'Personal Information',
      fields: ['personal_essay_topics', 'interests', 'career_goals', 'unique_circumstances'],
      taskTitle: 'Complete Personal Statement Information',
      priority: 'medium',
      estimatedMinutes: 60
    }
  };

  /**
   * Generate profile completion tasks based on missing fields
   */
  static async generateProfileTasks(supabase, userId) {
    try {
      // Get current profile data
      const profileData = await profileDataService.getCompleteProfile(userId);
      const completeness = profileDataService.calculateCompleteness(profileData);
      
      // Generate tasks for incomplete sections
      const tasks = [];
      
      for (const [sectionKey, sectionConfig] of Object.entries(this.PROFILE_SECTIONS)) {
        const sectionCompleteness = completeness.sections[sectionKey];
        
        // Generate task if section is not fully complete
        if (sectionCompleteness && sectionCompleteness.percentage < 100) {
          const task = this.createProfileTask(
            userId,
            sectionKey,
            sectionConfig,
            sectionCompleteness
          );
          tasks.push(task);
        }
      }

      // Sort tasks by priority
      tasks.sort((a, b) => {
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      return tasks;
    } catch (error) {
      console.error('Error generating profile tasks:', error);
      throw error;
    }
  }

  /**
   * Create a profile completion task
   */
  static createProfileTask(userId, sectionKey, sectionConfig, completeness) {
    // Use the correct properties from section completeness data
    const totalFields = completeness.total || 0;
    const filledFields = completeness.complete || 0;
    const missingFieldsCount = totalFields - filledFields;
    const percentComplete = Math.round(completeness.percentage || 0);
    
    // Ensure we have valid numbers to prevent NaN
    const validMissingCount = Math.max(0, missingFieldsCount);
    
    // Generate task description with specific missing fields
    const description = `Complete ${validMissingCount} missing field${validMissingCount !== 1 ? 's' : ''} in your ${sectionConfig.label} section. This section is currently ${percentComplete}% complete.`;
    
    // Calculate due date based on priority
    const daysUntilDue = sectionConfig.priority === 'high' ? 7 : 14;
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + daysUntilDue);
    
    // Use DateSynchronizationService to get synchronized dates
    const dates = DateSynchronizationService.getSynchronizedDates(
      'due_date',
      dueDate.toISOString().split('T')[0],
      'profile'
    );
    
    // Use StatusMappingService to get synchronized status
    const status = StatusMappingService.getSynchronizedStatus('workflow_phase', 'ready');
    
    return {
      user_id: userId,
      title: sectionConfig.taskTitle,
      description: description,
      category: 'profile',
      // epic_id removed - using category-based architecture
      priority: sectionConfig.priority,
      ...dates,
      ...status,
      estimated_minutes: sectionConfig.estimatedMinutes,
      profile_section: sectionKey,
      percent_complete: percentComplete,
      missing_fields: validMissingCount,
      source_type: 'profile_generation',
      source_id: `profile_${sectionKey}`,
      metadata: {
        section: sectionKey,
        completeness: completeness
      }
    };
  }

  /**
   * Sync profile tasks with timeline
   * (Method name kept for compatibility; epics deprecated in favor of categories)
   *
   * ENHANCED: Now returns structured result with success/error information
   * for user-visible notifications
   */
  static async syncProfileTasksWithEpic(supabase, userId) {
    const startTime = Date.now();

    try {
      // Generate current profile tasks
      const profileTasks = await this.generateProfileTasks(supabase, userId);

      // Get existing profile tasks from timeline_items
      const { data: existingTasks, error: fetchError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('category', 'profile')
        .eq('source_type', 'profile_generation');

      if (fetchError) {
        console.error('[Profile Sync] Error fetching existing tasks:', fetchError);
        return {
          success: false,
          error: `Failed to fetch existing tasks: ${fetchError.message}`,
          errorCode: 'FETCH_ERROR',
          tasksCreated: 0,
          tasksUpdated: 0,
          tasksCompleted: 0,
          duration: Date.now() - startTime
        };
      }

      // Create a map of existing tasks by source_id
      const existingTaskMap = new Map();
      existingTasks?.forEach(task => {
        existingTaskMap.set(task.source_id, task);
      });

      // Process each generated task
      const tasksToInsert = [];
      const tasksToUpdate = [];

      for (const task of profileTasks) {
        const existingTask = existingTaskMap.get(task.source_id);

        if (existingTask) {
          // Update existing task if profile completeness changed
          if (existingTask.percent_complete !== task.percent_complete ||
              existingTask.missing_fields !== task.missing_fields) {
            tasksToUpdate.push({
              id: existingTask.id,
              ...task,
              updated_at: new Date().toISOString()
            });
          }
          // Remove from map to track what needs deletion
          existingTaskMap.delete(task.source_id);
        } else {
          // Insert new task
          tasksToInsert.push({
            ...task,
            created_at: new Date().toISOString()
          });
        }
      }

      // Mark completed tasks that are no longer needed (100% complete sections)
      const tasksToComplete = Array.from(existingTaskMap.values()).filter(task => {
        // Task exists but wasn't in the generated list, meaning section is complete
        return task.workflow_phase !== 'complete';
      });

      // Execute database operations
      const operations = [];
      let insertResult = null;
      let updateResults = [];
      let completeResult = null;

      if (tasksToInsert.length > 0) {
        insertResult = await supabase
          .from('timeline_items')
          .insert(tasksToInsert);

        if (insertResult.error) {
          console.error('[Profile Sync] Error inserting tasks:', insertResult.error);
          return {
            success: false,
            error: `Failed to create profile tasks: ${insertResult.error.message}`,
            errorCode: 'INSERT_ERROR',
            tasksCreated: 0,
            tasksUpdated: 0,
            tasksCompleted: 0,
            duration: Date.now() - startTime
          };
        }
      }

      if (tasksToUpdate.length > 0) {
        for (const task of tasksToUpdate) {
          const updateResult = await supabase
            .from('timeline_items')
            .update(task)
            .eq('id', task.id);

          updateResults.push(updateResult);

          if (updateResult.error) {
            console.error('[Profile Sync] Error updating task:', updateResult.error);
            // Continue with other updates instead of failing completely
          }
        }
      }

      if (tasksToComplete.length > 0) {
        const completionStatus = StatusMappingService.getSynchronizedStatus('workflow_phase', 'complete');
        completeResult = await supabase
          .from('timeline_items')
          .update({
            ...completionStatus,
            completion_date: new Date().toISOString()
          })
          .in('id', tasksToComplete.map(t => t.id));

        if (completeResult.error) {
          console.error('[Profile Sync] Error completing tasks:', completeResult.error);
          // Don't fail sync if completion fails, but log it
        }
      }

      // Count successful operations
      const successfulUpdates = updateResults.filter(r => !r.error).length;
      const updateErrors = updateResults.filter(r => r.error);

      // Determine overall success
      const hasErrors = updateErrors.length > 0 || (completeResult && completeResult.error);

      return {
        success: true,
        tasksCreated: tasksToInsert.length,
        tasksUpdated: successfulUpdates,
        tasksCompleted: tasksToComplete.length,
        total: profileTasks.length,
        duration: Date.now() - startTime,
        warnings: hasErrors ? `${updateErrors.length} task(s) failed to update` : null
      };
    } catch (error) {
      console.error('[Profile Sync] Unexpected error:', error);
      return {
        success: false,
        error: error.message || 'Unknown error during profile sync',
        errorCode: 'UNEXPECTED_ERROR',
        tasksCreated: 0,
        tasksUpdated: 0,
        tasksCompleted: 0,
        duration: Date.now() - startTime
      };
    }
  }

  /**
   * Get profile completion summary for display
   */
  static async getProfileCompletionSummary(userId) {
    try {
      const profileData = await profileDataService.getProfileData(userId);
      const completeness = profileDataService.calculateCompleteness(profileData);
      
      // Calculate critical missing fields (high priority sections)
      const criticalSections = ['basicInfo', 'schoolInfo', 'academicPerformance', 'extracurriculars'];
      let criticalMissing = 0;
      
      criticalSections.forEach(section => {
        const sectionData = completeness.sections[section];
        if (sectionData) {
          criticalMissing += Math.max(0, (sectionData.total || 0) - (sectionData.complete || 0));
        }
      });
      
      return {
        overallPercentage: completeness.overall || 0,
        totalFields: completeness.totalFields || 0,
        filledFields: completeness.completedFields || 0,
        missingFields: Math.max(0, (completeness.totalFields || 0) - (completeness.completedFields || 0)),
        criticalMissingFields: criticalMissing,
        sections: completeness.sections,
        isComplete: (completeness.overall || 0) === 100,
        isReadyForSubmission: (completeness.overall || 0) >= 85 && criticalMissing === 0
      };
    } catch (error) {
      console.error('Error getting profile completion summary:', error);
      throw error;
    }
  }
}

export default ProfileTaskGenerationService;